import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { GroomingCommandPalette } from "@/components/grooming/command-palette";
import { GroomingToasts } from "@/components/grooming/grooming-toasts";
import { HelpPanelHost } from "@/components/toolbox/help/help-panel";
import { UpdateBand } from "@/components/toolbox/update-band";
import { writeLastProject } from "@/lib/grooming/last-project";
import { projectFromPath } from "@/lib/grooming/paths";
import { toolboxTitleBar, useToolboxBridge } from "@/lib/toolbox/bridge";
import { isFormulatorPath } from "@/lib/toolbox/tools";
import { isHelpShortcut, useHelpPanelStore } from "@/stores/help-panel-store";
import { IconRail } from "./icon-rail";
import { TitleBar } from "./title-bar";

/**
 * The SpecTree Toolbox window chrome (epic PI-SPEC-SW-E280, feature
 * PI-SPEC-SW-1399; design "SpecTree Grooming Desktop", `.win`): a 42px
 * title bar, the 76px icon rail and the page. AppLayout renders it instead
 * of SpecTree's sidebar and top bar when a grooming or toolbox page runs
 * inside SpecTree Toolbox; a browser keeps SpecTree's normal layout.
 * Formulator pages (epic PI-SPEC-SW-E282) share the frame but not Grooming's
 * command palette: the title bar hides its search there, so Cmd/Ctrl+K stays
 * free for the Formulator.
 *
 * The unsigned-pilot update band (epic PI-SPEC-SW-E288, feature
 * PI-SPEC-SW-1468, frame 1g) sits once under the title bar, across the full
 * width, on every page the frame wraps; it cannot be dismissed and renders
 * nothing while the app reports no update.
 *
 * Every `/grooming/<project>` page it shows is remembered as the last project
 * on this computer (epic PI-SPEC-SW-E292, feature PI-SPEC-SW-1495), so
 * Toolbox home and the command palette know it.
 *
 * Toolbox Help (epic PI-SPEC-SW-E302, feature PI-SPEC-SW-1557, ER-1655
 * Resolved Decisions 4 and 7) opens as a 408 px panel over the right of the
 * page, beside the rail: from the rail's Help item, or F1 and Ctrl+/ on every
 * page the frame wraps (Setup and Toolbox home with no role included).
 */
export function ToolboxFrame({ children }: { children: ReactNode }) {
  const bridge = useToolboxBridge();
  const { pathname } = useLocation();
  const project = projectFromPath(pathname);

  useEffect(() => {
    if (project) writeLastProject(project);
  }, [project]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isHelpShortcut(event)) return;
      event.preventDefault();
      useHelpPanelStore.getState().toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-[#09090B] text-[13px] text-[#FAFAFA]"
      data-testid="toolbox-frame"
    >
      <TitleBar
        titleBar={toolboxTitleBar(bridge)}
        project={project}
        appVersion={bridge?.app.version}
      />
      <UpdateBand />
      <div className="relative flex min-h-0 flex-1">
        <IconRail project={project} />
        <main className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">{children}</main>
        <HelpPanelHost />
      </div>
      {!isFormulatorPath(pathname) && <GroomingCommandPalette />}
      <GroomingToasts />
    </div>
  );
}
